import type { RunStageDetail } from "../types.js";
import { summarizeModelCalls, validUsage } from "../model-call-metrics.js";

const statusLabel = { running: "尚无结束回执", succeeded: "已返回输出", failed: "请求失败" };
const number = (value: number) => value.toLocaleString("zh-CN");

/** Observed requests, not inferred role counts, review votes or billing totals. */
export function ModelCallReceipt({ detail }: { detail: RunStageDetail }) {
  const summary = summarizeModelCalls(detail.modelCalls);
  return <section className="stage-detail-sections" aria-label="模型调用与实际用量">
    <header><h3>模型调用与实际用量</h3><span>本阶段持久化记录</span></header>
    {!summary.requestCount ? <p>未记录模型请求用量，不能据此认定零调用、零开销或已完成。历史记录不会倒推出实际 token。</p> : <>
      <div className="stage-detail-metrics">
        <span><small>已记录会话请求</small><strong>{number(summary.requestCount)} 次</strong></span>
        <span><small>SDK 重试事件</small><strong>{summary.measuredCalls ? `${number(summary.sdkRetryEvents)} 次` : "未记录"}</strong></span>
        <span><small>已报告 token</small><strong>{summary.usage ? number(summary.usage.total) : "未报告"}</strong></span>
        <span><small>失败 / 无结束回执</small><strong>{summary.failed} / {summary.unfinished}</strong></span>
      </div>
      <p>用量覆盖 {summary.usageReports} / {summary.assistantMessages} 个已结束模型响应；{summary.measuredCalls} / {summary.requestCount} 次请求有采集回执。未返回或缺失用量的响应不按零计费。</p>
      <p>一次文学编辑的三个维度只算一次会话请求；格式修复、备用模型的再次请求单独记录。SDK 重试事件不等于全部底层 HTTP 请求。收到输出不代表审稿通过。</p>
      {summary.calls.map(call => <details key={call.id}>
        <summary><span><strong>{detail.agents.find(a => a.id === call.agentId || a.id.startsWith(`${call.agentId}:`))?.name ?? call.agentId}</strong><small>{statusLabel[call.status]}</small></span>
          <span>{call.elapsedMs !== undefined && Number.isFinite(call.elapsedMs) ? `${(call.elapsedMs / 1000).toFixed(1)} 秒` : "耗时未确认"}</span></summary>
        <pre>{[
          `请求模型：${call.model || "未记录"}`, `请求：${call.id}`, `会话：${call.sessionId}`,
          `开始：${call.startedAt}`, `结束：${call.finishedAt || "尚无结束回执；可能仍在执行、已中断或回执保存失败"}`,
          call.telemetry?.version === "sdk-usage-v1" && validUsage(call.telemetry.usage) ? `SDK 已报告用量：输入 ${call.telemetry.usage.input} / 输出 ${call.telemetry.usage.output} / 缓存读取 ${call.telemetry.usage.cacheRead} / 缓存写入 ${call.telemetry.usage.cacheWrite} / 合计 ${call.telemetry.usage.total}` : "SDK 未报告有效用量；不使用字符估算冒充实际 token。",
          `输入指纹：${call.inputHash}`
        ].join("\n")}</pre>
      </details>)}
      <p>耗时按各次请求记录，不把并行耗时相加当作章节总耗时；用量来自 SDK 报告，不是服务商账单或文学质量证明。</p>
    </>}
    {summary.invalidRecords > 0 && <p role="status">另有 {summary.invalidRecords} 条无效调用回执未计入统计，未将它们视为成功。</p>}
  </section>;
}
